import React, { useState } from 'react';
import { 
  ShieldCheck, Share2, UserCheck, FileText, 
  BadgeDollarSign, ChevronRight 
} from 'lucide-react';

const SOCBenefits = () => {
  const benefits = [
    { 
      id: 1,
      name: "Complete 24/7 Visibility", 
      icon: <ShieldCheck size={20} />,
      details: "Centralized monitoring across endpoints, network, cloud, and logs. We provide real-time oversight to ensure no threat goes unnoticed across your entire infrastructure."
    },
    { 
      id: 2,
      name: "Seamless Integration", 
      icon: <Share2 size={20} />,
      details: "Works with your current security tools — no rip-and-replace. We integrate directly into your existing stack to maximize your current investments."
    },
    { 
      id: 3,
      name: "Expert Oversight", 
      icon: <UserCheck size={20} />,
      details: "Dedicated analysts handle event correlation, alerting, and basic triage, acting as an extension of your team to eliminate false positives."
    },
    { 
      id: 4,
      name: "Compliance & Reporting", 
      icon: <FileText size={20} />,
      details: "Ongoing support for standards like SOC 2, ISO 27001, NIST, and more. Get boardroom-ready reports and maintain continuous compliance audits."
    },
    { 
      id: 5,
      name: "Cost-Effective Coverage", 
      icon: <BadgeDollarSign size={20} />,
      details: "Get enterprise-level 24/7 monitoring at a fraction of the cost of building, staffing, and maintaining your own Security Operations Center."
    },
  ];

  const [activeBenefit, setActiveBenefit] = useState(benefits[0]);

  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4">
        {/* Changed gap to 8 and items to stretch */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          
          {/* LEFT COLUMN: Reduced width from span-5 to span-4 */}
          <div className="lg:col-span-4 relative group">
            <div className="bg-gradient-to-br from-[#2e8139ff] to-[#052e1f] text-white p-8 md:p-10 shadow-2xl relative clip-path-polygon h-full"> 
              <style dangerouslySetInnerHTML={{ __html: `
                .clip-path-polygon {
                  clip-path: polygon(0 0, 100% 0, 100% 85%, 85% 100%, 0 100%);
                }
              `}} />
              
              <h2 className="text-3xl md:text-4xl font-extrabold leading-tight mb-6">
                What You Get with <br />
                <span className="opacity-70">SecuWall SOC-as-a-Service</span>
              </h2>

              <p className="text-base text-blue-100/90 leading-relaxed mb-8">
                In today’s evolving landscape of data protection and privacy regulations, 
                there is no single solution that fits every cybersecurity requirement. 
                SecuWall SOC-as-a-Service delivers a tailored, industry-focused approach.
              </p>
            </div>
          </div>

          {/* RIGHT COLUMN: Increased width from span-7 to span-8 */}
          <div className="lg:col-span-8 flex flex-col pt-6 lg:pt-0">
            <div className="mb-6 border-b border-gray-100 pb-4">
               <h4 className="text-blue-600 font-bold tracking-[0.3em] uppercase text-xs">
                 Core Platform Advantages
               </h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
              {/* Headings List */}
              <div className="flex flex-col justify-center space-y-2">
                {benefits.map((benefit) => (
                  <div 
                    key={benefit.id}
                    onMouseEnter={() => setActiveBenefit(benefit)}
                    className={`flex items-center justify-between p-4 rounded-lg transition-all cursor-pointer border ${
                      activeBenefit.id === benefit.id 
                      ? "bg-green-50 border-green-200 shadow-sm translate-x-1" 
                      : "bg-white border-transparent hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className={activeBenefit.id === benefit.id ? "text-green-600" : "text-gray-400"}>
                        {benefit.icon}
                      </span>
                      <span className={`font-bold text-sm ${activeBenefit.id === benefit.id ? "text-green-900" : "text-gray-700"}`}>
                        {benefit.name}
                      </span>
                    </div>
                    <ChevronRight size={14} className={activeBenefit.id === benefit.id ? "text-green-500" : "opacity-0"} />
                  </div>
                ))}
              </div>

              {/* Description Box */}
              <div className="relative bg-[#052e1f] rounded-2xl p-8 text-white shadow-xl overflow-hidden flex items-center min-h-[300px]">
                {/* Decorative Background Icon */}
                <div className="absolute -right-4 -bottom-4 text-white opacity-5 scale-[2.5] pointer-events-none">
                  {activeBenefit.icon}
                </div>

                <div key={activeBenefit.id} className="relative z-10 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <h3 className="text-xl font-bold text-green-400 mb-4 flex items-center gap-2">
                    {activeBenefit.icon} {activeBenefit.name}
                  </h3>
                  <div className="w-10 h-1 bg-green-500 mb-6 rounded-full" />
                  <p className="text-gray-200 leading-relaxed text-base font-medium">
                    {activeBenefit.details}
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default SOCBenefits;